import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Badge, Spin, message, Tabs } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ApiOutlined } from '@ant-design/icons';
import { devicesApi, meters, protocols } from '../../services/api';
import { isViewer } from '../../utils/auth';

function formatTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('zh-CN', { hour12: false });
}

export default function Devices() {
  // viewer 只读：设备测试/映射等写操作按钮禁用（后端 403 兜底）
  const readOnly = isViewer();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [health, setHealth] = useState(null);
  const [iecStatus, setIecStatus] = useState(null);
  const [iecDevices, setIecDevices] = useState([]);

  const loadData = async () => {
    try {
      const [listRes, healthRes, iecRes] = await Promise.all([
        devicesApi.list(),
        devicesApi.health(),
        protocols.iec61850Status().catch(() => null),
      ]);
      setDevices((listRes.data || []).map(d => ({
        key: d.id,
        ...d,
        last_online: formatTime(d.last_online),
      })));
      setHealth(healthRes.data);
      if (iecRes?.data) setIecStatus(iecRes.data);
    } catch (err) {
      message.error(err.message || '加载设备数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleTestModbus = async (record) => {
    if (!record.ip) {
      message.warning('该设备未配置 IP，请先在后台更新 Modbus 配置');
      return;
    }
    try {
      const res = await meters.testModbus(record.id);
      if (res.success) {
        message.success(`Modbus 测试成功：功率 ${res.data?.active_power ?? '-'} kW`);
      } else {
        message.error(res.message || 'Modbus 测试失败');
      }
      loadData();
    } catch (err) {
      message.error(err.message || 'Modbus 测试失败');
    }
  };

  const handleIecDiscover = async () => {
    try {
      const res = await protocols.iec61850Discover();
      setIecDevices(res.data?.devices || []);
      message.success(`发现 ${res.data?.devices?.length || 0} 台 IED 设备`);
    } catch (err) {
      message.error(err.message || '扫描失败');
    }
  };

  const handleIecMap = async (record) => {
    try {
      await protocols.iec61850MapMeter(record.id);
      message.success('IEC 61850 点位映射完成');
      loadData();
    } catch (err) {
      message.error(err.message || '映射失败');
    }
  };

  const columns = [
    { title: '设备名称', dataIndex: 'name' },
    { title: '编码', dataIndex: 'code' },
    { title: '类型', dataIndex: 'type', render: t => <Tag>{t}</Tag> },
    { title: '状态', dataIndex: 'status', render: v => <Badge status={v === '在线' ? 'success' : 'error'} text={v} /> },
    { title: 'IP地址', dataIndex: 'ip', render: v => v || '-' },
    { title: '固件版本', dataIndex: 'firmware' },
    { title: '最后在线', dataIndex: 'last_online' },
    { title: '操作', render: (_, record) => (
      <>
        <Button size="small" onClick={() => handleTestModbus(record)} disabled={readOnly || !record.protocol?.includes('Modbus')} style={{ marginRight: 8 }}>
          测试Modbus
        </Button>
        <Button size="small" icon={<ApiOutlined />} onClick={() => handleIecMap(record)} disabled={readOnly}>IEC61850</Button>
      </>
    )},
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="设备总数" value={health?.total ?? devices.length} suffix="台" /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="在线" value={health?.online ?? 0} suffix="台" valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="离线" value={health?.offline ?? 0} suffix="台" valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="30天在线率" value={health?.uptime_30d != null ? parseFloat(health.uptime_30d) : '—'} suffix={health?.uptime_30d != null ? '%' : ''} precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Card title="设备管理" style={{ marginTop: 16 }} size="small" extra={<Button type="primary" onClick={loadData}>同步设备</Button>}>
        <Table columns={columns} dataSource={devices} pagination={false} size="small" />
      </Card>

      {iecStatus && (
        <Card
          title="IEC 61850 协议接入"
          style={{ marginTop: 16 }}
          size="small"
          extra={<Button onClick={handleIecDiscover}>扫描 IED 设备</Button>}
        >
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col><Tag color="green">适配器 {iecStatus.adapter_version}</Tag></Col>
            <Col><Tag>MMS 读取</Tag><Tag>GOOSE 订阅</Tag><Tag>SCL 导入</Tag></Col>
            <Col><span style={{ fontSize: 12, color: '#999' }}>已接入 {iecStatus.connected_devices} 台</span></Col>
          </Row>
          {iecDevices.length > 0 && (
            <Table
              size="small"
              pagination={false}
              dataSource={iecDevices.map((d, i) => ({ ...d, key: i }))}
              columns={[
                { title: 'IED名称', dataIndex: 'ied_name' },
                { title: 'IP', dataIndex: 'ip' },
                { title: '厂商', dataIndex: 'vendor' },
                { title: '状态', dataIndex: 'status', render: (s) => <Tag color={s === '在线' ? 'green' : 'default'}>{s}</Tag> },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
